import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { InvestorCommunication } from "../../../api/types/investorCommunicationsTypes";
import type { InvestorCommunicationStatusTransition } from "./investorCommunicationStatusHelper";
import NewsPageNotLiveDialogAlert from "./NewsPageNotLiveDialogAlert";

const changeInvestorCommunicationStatus = withErrorHandling(adminApi.changeInvestorCommunicationStatus);

interface Props {
  investorCommunicationId: string;
  expectedEtag: string;
  statusTransition: InvestorCommunicationStatusTransition;
  onClose: () => void;
  onUpdated: (investorCommunication: InvestorCommunication) => void;
}

const ChangeInvestorCommunicationStatusDialog = ({
  investorCommunicationId,
  expectedEtag,
  statusTransition,
  onClose,
  onUpdated,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    const [investorCommunication, error] = await changeInvestorCommunicationStatus(investorCommunicationId, {
      status: statusTransition.to,
      expectedEtag,
    });
    setSaving(false);

    if (error) {
      logError(error, "[ChangeInvestorCommunicationStatusDialog] changeInvestorCommunicationStatus");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Status updated successfully");
    onUpdated(investorCommunication);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{statusTransition.label}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <statusTransition.DialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography noWrap variant="h6">
              {statusTransition.label}
            </Typography>
            {statusTransition.detailLines.map((line) => (
              <Typography key={line} color="textSecondary" sx={{ textAlign: "center" }}>
                {line}
              </Typography>
            ))}
            {statusTransition.showNewsNotLiveWarning && <NewsPageNotLiveDialogAlert />}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelDisabled={saving}>
        <Button variant="contained" loading={saving} onClick={handleSubmit}>
          {statusTransition.dialogActionLabel}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default ChangeInvestorCommunicationStatusDialog;
